import React, { useState, useEffect, useRef } from 'react';
import { X, ZoomIn, Move, RotateCw, Save, Loader2, RefreshCw, Check, Plus, Trash2 } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';

const PlacementEditor = ({ product, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingPosition, setAddingPosition] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const canvasRef = useRef(null);
  const designImageRef = useRef(null);

  // Position state
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [availablePositions, setAvailablePositions] = useState([]);
  const [placements, setPlacements] = useState({});
  const [originalPlacements, setOriginalPlacements] = useState({});

  // Current position's placement state
  const [scale, setScale] = useState(1);
  const [posX, setPosX] = useState(0.5);
  const [posY, setPosY] = useState(0.5);
  const [angle, setAngle] = useState(0);

  // Image loading states
  const [designImageLoaded, setDesignImageLoaded] = useState(false);

  // Post-save state for showing updated mockup
  const [saveComplete, setSaveComplete] = useState(false);
  const [pollingMockup, setPollingMockup] = useState(false);
  const [updatedMockupUrl, setUpdatedMockupUrl] = useState(null);
  const [updatedMockupLoaded, setUpdatedMockupLoaded] = useState(false);
  const [originalMockupUrl, setOriginalMockupUrl] = useState(null);
  const [savedProductData, setSavedProductData] = useState(null);

  // Load product details on mount
  useEffect(() => {
    // Use printifyProductId if available, otherwise fall back to id
    const productId = product.printifyProductId || product.id;
    if (productId) {
      loadProductDetails(productId);
    } else {
      setError('No product ID available');
      setLoading(false);
    }
  }, [product.printifyProductId, product.id]);

  // Load design image when URL is available
  useEffect(() => {
    if (productDetails?.designImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        designImageRef.current = img;
        setDesignImageLoaded(true);
      };
      img.onerror = () => {
        console.log('Failed to load design image');
        setDesignImageLoaded(false);
      };
      img.src = productDetails.designImageUrl;
    }
  }, [productDetails?.designImageUrl]);

  // Draw preview whenever placement changes
  useEffect(() => {
    drawPreview();
  }, [scale, posX, posY, angle, productDetails, designImageLoaded, selectedPosition, placements]);

  // Update sliders when position changes or placements are loaded
  useEffect(() => {
    if (selectedPosition && placements[selectedPosition]) {
      const p = placements[selectedPosition];
      console.log('Updating sliders for position:', selectedPosition, 'with values:', p);
      setScale(p.scale || 1);
      setPosX(p.x !== undefined ? p.x : 0.5);
      setPosY(p.y !== undefined ? p.y : 0.5);
      setAngle(p.angle || 0);
    } else if (selectedPosition) {
      // Reset to defaults for empty position
      setScale(0.85);
      setPosX(0.5);
      setPosY(0.5);
      setAngle(0);
    }
  }, [selectedPosition, placements]);

  const loadProductDetails = async (productId) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading product details for:', productId);
      const response = await amplifyApi.getProductDetails(productId);
      console.log('FULL getProductDetails response:', JSON.stringify(response, null, 2));
      if (response.success && response.product) {
        console.log('Product details loaded:', {
          id: response.product.id,
          availablePositions: response.product.availablePositions,
          placements: response.product.placements,
          designImageId: response.product.designImageId,
          mockupUrl: response.product.mockupUrl
        });

        setProductDetails(response.product);

        // Set placements by position
        let positionPlacements = response.product.placements || {};

        // If placements is empty but we have a designImageId, the product has a design
        // Try to use the currentPlacement (backwards compat) or create from available data
        if (Object.keys(positionPlacements).length === 0 && response.product.currentPlacement) {
          positionPlacements = {
            front: response.product.currentPlacement
          };
          console.log('Using currentPlacement fallback:', positionPlacements);
        }

        // Get available positions from API response OR derive from placements object
        let positions = response.product.availablePositions || [];

        // If availablePositions is empty but we have placements, derive positions from placements
        if (positions.length === 0 && Object.keys(positionPlacements).length > 0) {
          positions = Object.keys(positionPlacements).map(pos => ({
            position: pos,
            width: 4000,  // Default dimensions
            height: 4000
          }));
          console.log('Derived positions from placements:', positions.map(p => p.position));
        }

        // Final fallback: if still no positions, use front
        if (positions.length === 0) {
          positions = [{ position: 'front', width: 4000, height: 4000 }];
        }

        setAvailablePositions(positions);

        console.log('Setting placements state to:', JSON.stringify(positionPlacements, null, 2));
        setPlacements(positionPlacements);
        setOriginalPlacements(JSON.parse(JSON.stringify(positionPlacements)));

        // Select first position WITH A DESIGN, or first available
        const firstWithDesign = positions.find(p => positionPlacements[p.position] !== null && positionPlacements[p.position] !== undefined);
        const firstPosition = firstWithDesign?.position || positions[0]?.position || 'front';
        setSelectedPosition(firstPosition);

        console.log('Selected position:', firstPosition, 'Has design:', !!positionPlacements[firstPosition]);
        console.log('Placement for selected position:', JSON.stringify(positionPlacements[firstPosition], null, 2));

        // Load initial values - use explicit checks for 0 values
        if (positionPlacements[firstPosition]) {
          const p = positionPlacements[firstPosition];
          console.log('Loading initial values:', p);
          setScale(p.scale !== undefined ? p.scale : 1);
          setPosX(p.x !== undefined ? p.x : 0.5);
          setPosY(p.y !== undefined ? p.y : 0.5);
          setAngle(p.angle !== undefined ? p.angle : 0);
        }
      } else {
        console.error('Failed to load product details:', response);
        setError(response.error || 'Failed to load product details');
      }
    } catch (err) {
      setError(err.message || 'Failed to load product details');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPrintArea = () => {
    if (!selectedPosition || !availablePositions.length) return null;
    return availablePositions.find(p => p.position === selectedPosition);
  };

  const drawPreview = () => {
    const canvas = canvasRef.current;
    if (!canvas || !productDetails) return;

    const ctx = canvas.getContext('2d');
    const canvasSize = 320;
    canvas.width = canvasSize;
    canvas.height = canvasSize;

    // Clear canvas
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, canvasSize, canvasSize);

    // Get print area for selected position
    const printArea = getCurrentPrintArea();
    let printAreaAspect = 1;
    if (printArea && printArea.width && printArea.height) {
      printAreaAspect = printArea.width / printArea.height;
    }

    // Calculate print area display dimensions maintaining aspect ratio
    const maxPrintAreaSize = canvasSize - 60;
    let printAreaDisplayWidth, printAreaDisplayHeight;

    if (printAreaAspect >= 1) {
      printAreaDisplayWidth = maxPrintAreaSize;
      printAreaDisplayHeight = maxPrintAreaSize / printAreaAspect;
    } else {
      printAreaDisplayHeight = maxPrintAreaSize;
      printAreaDisplayWidth = maxPrintAreaSize * printAreaAspect;
    }

    const printAreaX = (canvasSize - printAreaDisplayWidth) / 2;
    const printAreaY = (canvasSize - printAreaDisplayHeight) / 2;

    // Draw print area background
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(printAreaX, printAreaY, printAreaDisplayWidth, printAreaDisplayHeight);

    // Draw print area outline with dashed border
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(printAreaX, printAreaY, printAreaDisplayWidth, printAreaDisplayHeight);
    ctx.setLineDash([]);

    // Check if current position has a design
    const hasDesign = placements[selectedPosition] !== null && placements[selectedPosition] !== undefined;

    if (hasDesign) {
      // Calculate design position within print area
      const designCenterX = printAreaX + (posX * printAreaDisplayWidth);
      const designCenterY = printAreaY + (posY * printAreaDisplayHeight);

      // Draw the design image or placeholder
      ctx.save();
      ctx.translate(designCenterX, designCenterY);
      ctx.rotate((angle * Math.PI) / 180);

      if (designImageLoaded && designImageRef.current) {
        const designImg = designImageRef.current;
        const imgAspect = designImg.width / designImg.height;
        let designDisplayWidth = printAreaDisplayWidth * scale;
        let designDisplayHeight = designDisplayWidth / imgAspect;

        ctx.globalAlpha = 0.9;
        ctx.drawImage(
          designImg,
          -designDisplayWidth / 2,
          -designDisplayHeight / 2,
          designDisplayWidth,
          designDisplayHeight
        );

        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.strokeRect(
          -designDisplayWidth / 2,
          -designDisplayHeight / 2,
          designDisplayWidth,
          designDisplayHeight
        );

        const handleSize = 6;
        ctx.fillStyle = '#3b82f6';
        const corners = [
          [-designDisplayWidth / 2, -designDisplayHeight / 2],
          [designDisplayWidth / 2, -designDisplayHeight / 2],
          [-designDisplayWidth / 2, designDisplayHeight / 2],
          [designDisplayWidth / 2, designDisplayHeight / 2]
        ];
        corners.forEach(([cx, cy]) => {
          ctx.fillRect(cx - handleSize / 2, cy - handleSize / 2, handleSize, handleSize);
        });
      } else {
        const designSize = printAreaDisplayWidth * scale;
        ctx.fillStyle = '#3b82f6';
        ctx.globalAlpha = 0.6;
        ctx.fillRect(-designSize / 2, -designSize / 2, designSize, designSize);

        ctx.globalAlpha = 1;
        ctx.strokeStyle = '#1d4ed8';
        ctx.lineWidth = 2;
        ctx.strokeRect(-designSize / 2, -designSize / 2, designSize, designSize);

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-12, 0);
        ctx.lineTo(12, 0);
        ctx.moveTo(0, -12);
        ctx.lineTo(0, 12);
        ctx.stroke();

        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Design', 0, 4);
      }

      ctx.restore();

      // Draw center reference crosshairs
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(printAreaX, printAreaY + printAreaDisplayHeight / 2);
      ctx.lineTo(printAreaX + printAreaDisplayWidth, printAreaY + printAreaDisplayHeight / 2);
      ctx.moveTo(printAreaX + printAreaDisplayWidth / 2, printAreaY);
      ctx.lineTo(printAreaX + printAreaDisplayWidth / 2, printAreaY + printAreaDisplayHeight);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      // No design - show empty state
      ctx.fillStyle = '#94a3b8';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No design on this position', canvasSize / 2, canvasSize / 2);
      ctx.font = '10px sans-serif';
      ctx.fillText('Click "Add Design" to add your design here', canvasSize / 2, canvasSize / 2 + 16);
    }

    // Labels
    ctx.fillStyle = '#64748b';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`Print Area (${selectedPosition || 'front'})`, canvasSize / 2, canvasSize - 8);

    if (printArea) {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '9px sans-serif';
      ctx.fillText(`${printArea.width} × ${printArea.height}px`, canvasSize / 2, 14);
    }
  };

  const handleAddDesign = async () => {
    if (!selectedPosition) return;

    setAddingPosition(true);
    setError(null);

    try {
      const productId = product.printifyProductId || product.id;
      const response = await amplifyApi.addDesignToPosition(
        productId,
        selectedPosition,
        { scale: 0.85, x: 0.5, y: 0.5, angle: 0 }
      );

      if (response.success) {
        // Update local state
        setPlacements(prev => ({
          ...prev,
          [selectedPosition]: { scale: 0.85, x: 0.5, y: 0.5, angle: 0 }
        }));
        setScale(0.85);
        setPosX(0.5);
        setPosY(0.5);
        setAngle(0);
      } else {
        setError(response.error || 'Failed to add design');
      }
    } catch (err) {
      setError(err.message || 'Failed to add design');
    } finally {
      setAddingPosition(false);
    }
  };

  const handleRemoveDesign = async () => {
    if (!selectedPosition) return;

    if (!window.confirm(`Remove design from "${selectedPosition}" position? This cannot be undone.`)) {
      return;
    }

    setRemoving(true);
    setError(null);

    try {
      const productId = product.printifyProductId || product.id;
      const response = await amplifyApi.removeDesignFromPosition(productId, selectedPosition);

      if (response.success) {
        // Update local state
        setPlacements(prev => ({
          ...prev,
          [selectedPosition]: null
        }));
        setOriginalPlacements(prev => ({
          ...prev,
          [selectedPosition]: null
        }));

        // Reset sliders to defaults
        setScale(0.85);
        setPosX(0.5);
        setPosY(0.5);
        setAngle(0);

        // Trigger mockup polling flow (same as handleSave)
        setOriginalMockupUrl(productDetails?.mockupUrl);
        setSaveComplete(true);
        setPollingMockup(true);
        pollForUpdatedMockup();
      } else {
        setError(response.error || 'Failed to remove design');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove design');
    } finally {
      setRemoving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPosition) return;

    setSaving(true);
    setError(null);
    try {
      const productId = product.printifyProductId || product.id;

      console.log('Saving placement:', {
        productId,
        selectedPosition,
        placement: { scale, x: posX, y: posY, angle },
        currentPlacements: placements,
        hasDesign: placements[selectedPosition] !== null && placements[selectedPosition] !== undefined
      });

      const response = await amplifyApi.updateProductPlacement(
        productId,
        { scale, x: posX, y: posY, angle },
        selectedPosition
      );

      console.log('Save response:', response);
      console.log('Save verified by Printify:', response.saveVerified);
      console.log('Actual saved placements:', JSON.stringify(response.product?.actualSavedPlacements, null, 2));

      // Check if Printify actually saved our values
      if (response.saveVerified === false) {
        console.error('WARNING: Printify did not save the placement values!');
        console.error('Expected:', { scale, x: posX, y: posY, angle });
        console.error('Got:', response.product?.actualSavedPlacements?.[selectedPosition]);
      }

      if (response.success) {
        // Update local placements
        setPlacements(prev => ({
          ...prev,
          [selectedPosition]: { scale, x: posX, y: posY, angle }
        }));
        setOriginalPlacements(prev => ({
          ...prev,
          [selectedPosition]: { scale, x: posX, y: posY, angle }
        }));

        // Store the saved product data - don't call onSave yet, wait for user to click Done
        setSavedProductData(response.product);

        // Check if save was verified
        if (response.saveVerified === false) {
          setError('Warning: The placement may not have been saved by Printify. Please try again.');
        } else {
          // Save the current mockup URL before polling for new one
          setOriginalMockupUrl(productDetails?.mockupUrl);
          setSaveComplete(true);
          setPollingMockup(true);

          // Start polling for updated mockup
          pollForUpdatedMockup();
        }
      } else {
        setError(response.error || 'Failed to update placement');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to update placement');
    } finally {
      setSaving(false);
    }
  };

  // Poll for updated mockup after save
  const pollForUpdatedMockup = async () => {
    const productId = product.printifyProductId || product.id;
    const maxAttempts = 6; // Poll for up to 30 seconds (6 * 5s)
    let attempts = 0;

    const poll = async () => {
      attempts++;
      try {
        const response = await amplifyApi.getProductDetails(productId);
        if (response.success && response.product?.mockupUrl) {
          const newMockupUrl = response.product.mockupUrl;

          // Check if mockup URL has changed
          if (newMockupUrl !== originalMockupUrl) {
            // URL changed - use the new URL with cache buster
            setUpdatedMockupLoaded(false); // Reset loaded state for new image
            setUpdatedMockupUrl(`${newMockupUrl}${newMockupUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
            setPollingMockup(false);
            return;
          }

          // After 3 attempts (15 seconds), assume Printify has regenerated the image
          // and show it with a cache buster even if URL didn't change
          if (attempts >= 3) {
            setUpdatedMockupLoaded(false); // Reset loaded state for new image
            setUpdatedMockupUrl(`${newMockupUrl}${newMockupUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
            setPollingMockup(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error polling for mockup:', err);
      }

      // Continue polling if we haven't exceeded max attempts
      if (attempts < maxAttempts) {
        setTimeout(poll, 5000); // Poll every 5 seconds
      } else {
        // Give up polling but still show success with cache-busted URL
        if (originalMockupUrl) {
          setUpdatedMockupLoaded(false); // Reset loaded state for new image
          setUpdatedMockupUrl(`${originalMockupUrl}${originalMockupUrl.includes('?') ? '&' : '?'}t=${Date.now()}`);
        }
        setPollingMockup(false);
      }
    };

    // Start polling after a short delay to give Printify time to start regenerating
    setTimeout(poll, 5000);
  };

  // Handle Done button - notify parent with saved data and close modal
  const handleDone = () => {
    if (savedProductData) {
      onSave?.(savedProductData);
    }
    onClose();
  };

  const handleReset = () => {
    if (selectedPosition && originalPlacements[selectedPosition]) {
      const p = originalPlacements[selectedPosition];
      setScale(p.scale || 1);
      setPosX(p.x || 0.5);
      setPosY(p.y || 0.5);
      setAngle(p.angle || 0);
    } else {
      setScale(0.85);
      setPosX(0.5);
      setPosY(0.5);
      setAngle(0);
    }
  };

  const hasChanges = () => {
    if (!selectedPosition) return false;
    const original = originalPlacements[selectedPosition];
    if (!original) return placements[selectedPosition] !== null;

    return (
      Math.abs(scale - (original.scale || 1)) > 0.01 ||
      Math.abs(posX - (original.x || 0.5)) > 0.01 ||
      Math.abs(posY - (original.y || 0.5)) > 0.01 ||
      Math.abs(angle - (original.angle || 0)) > 0.1
    );
  };

  const hasDesignOnPosition = (pos) => {
    return placements[pos] !== null && placements[pos] !== undefined;
  };

  const formatPositionName = (pos) => {
    if (!pos) return 'Unknown';
    return pos.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const scalePresets = [
    { label: 'S', value: 0.5, title: 'Small (50%)' },
    { label: 'M', value: 0.75, title: 'Medium (75%)' },
    { label: 'L', value: 1, title: 'Large (100%)' },
    { label: 'XL', value: 1.25, title: 'Extra Large (125%)' }
  ];

  const hasDesign = placements[selectedPosition] !== null && placements[selectedPosition] !== undefined;
  const showPositionTabs = availablePositions.length > 1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Edit Placement</h2>
            <p className="text-sm text-slate-500">{product.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Position Tabs */}
        {showPositionTabs && !loading && !saveComplete && (
          <div className="px-4 pt-3 pb-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <div className="flex gap-2 flex-wrap">
              {availablePositions.map(pos => {
                const isSelected = selectedPosition === pos.position;
                const hasDesignHere = hasDesignOnPosition(pos.position);

                return (
                  <button
                    key={pos.position}
                    onClick={() => setSelectedPosition(pos.position)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                      ${isSelected
                        ? 'bg-blue-600 text-white'
                        : hasDesignHere
                          ? 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                          : 'bg-white text-slate-400 border border-dashed border-slate-300 hover:bg-slate-50'
                      }`}
                  >
                    {hasDesignHere ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                    {formatPositionName(pos.position)}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              <Check className="w-3 h-3 inline" /> = has design, <Plus className="w-3 h-3 inline" /> = can add design
            </p>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Save complete - show updated mockup */}
          {saveComplete ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="flex items-center gap-2 mb-4">
                <Check className="w-6 h-6 text-green-600" />
                <span className="text-lg font-semibold text-green-700">Placement Saved!</span>
              </div>

              {pollingMockup ? (
                <div className="text-center">
                  <div className="relative w-64 h-64 mx-auto mb-4 bg-slate-100 rounded-xl overflow-hidden">
                    {originalMockupUrl && (
                      <img
                        src={originalMockupUrl}
                        alt="Current mockup"
                        className="w-full h-full object-contain opacity-50"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
                        <p className="text-sm text-slate-600 mt-2">Generating new mockup...</p>
                        <p className="text-xs text-slate-400 mt-1">This may take 10-30 seconds</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : updatedMockupUrl ? (
                <div className="text-center">
                  <div className="w-72 h-72 mx-auto mb-4 bg-slate-100 rounded-xl overflow-hidden shadow-lg relative">
                    {/* Show old mockup faded + spinner until new image loads */}
                    {!updatedMockupLoaded && (
                      <>
                        {originalMockupUrl && (
                          <img
                            src={originalMockupUrl}
                            alt="Previous mockup"
                            className="absolute inset-0 w-full h-full object-contain opacity-30"
                          />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                          <div className="text-center">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
                            <p className="text-sm text-slate-600 mt-2">Loading new mockup...</p>
                          </div>
                        </div>
                      </>
                    )}
                    {/* New image - hidden until loaded, then fades in */}
                    <img
                      src={updatedMockupUrl}
                      alt="Updated mockup"
                      className={`w-full h-full object-contain transition-opacity duration-300 ${updatedMockupLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setUpdatedMockupLoaded(true)}
                    />
                  </div>
                  {updatedMockupLoaded && (
                    <p className="text-sm text-green-600 mb-4">New mockup is ready!</p>
                  )}
                </div>
              ) : (
                <div className="text-center mb-4">
                  <p className="text-sm text-slate-500">Mockup will update shortly. Refresh the page to see changes.</p>
                </div>
              )}

              <button
                onClick={handleDone}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Done
              </button>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-slate-500 mt-3">Loading product details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadProductDetails}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Preview */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-700">
                    {formatPositionName(selectedPosition)} Preview
                  </div>
                  {getCurrentPrintArea() && (
                    <span className="text-xs text-slate-400">
                      {getCurrentPrintArea().width} × {getCurrentPrintArea().height}px
                    </span>
                  )}
                </div>

                {/* Canvas Preview */}
                <div className="bg-slate-100 rounded-xl p-4 flex flex-col items-center">
                  <canvas
                    ref={canvasRef}
                    className="rounded-lg border border-slate-200 bg-white"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                  <p className="text-xs text-slate-400 mt-2">
                    {hasDesign
                      ? 'Drag sliders to adjust your design'
                      : 'No design on this position yet'}
                  </p>
                </div>

                {/* Current Mockup & Design Side by Side */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-medium text-slate-500 mb-1">Current Mockup</div>
                    <div className="bg-slate-50 rounded-lg p-2 aspect-square flex items-center justify-center">
                      <img
                        src={productDetails?.mockupUrl || product.mockupUrl}
                        alt={product.title}
                        className="max-w-full max-h-full object-contain rounded"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-slate-500 mb-1">Your Design</div>
                    <div className="bg-slate-50 rounded-lg p-2 aspect-square flex items-center justify-center">
                      {productDetails?.designImageUrl ? (
                        <img
                          src={productDetails.designImageUrl}
                          alt="Your design"
                          className="max-w-full max-h-full object-contain rounded"
                        />
                      ) : (
                        <div className="text-xs text-slate-400 text-center p-2">
                          Design preview unavailable
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Controls */}
              <div className="space-y-5">
                {!hasDesign ? (
                  /* Add Design Button for empty position */
                  <div className="flex flex-col items-center justify-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                    <Plus className="w-10 h-10 text-slate-300 mb-3" />
                    <p className="text-slate-500 text-sm mb-4 text-center">
                      No design on <strong>{formatPositionName(selectedPosition)}</strong> yet
                    </p>
                    <button
                      onClick={handleAddDesign}
                      disabled={addingPosition}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {addingPosition ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Add Design to {formatPositionName(selectedPosition)}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Scale Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <ZoomIn className="w-4 h-4" />
                          Scale
                        </label>
                        <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                          {(scale * 100).toFixed(0)}%
                        </span>
                      </div>

                      <div className="flex gap-2">
                        {scalePresets.map(preset => (
                          <button
                            key={preset.value}
                            onClick={() => setScale(preset.value)}
                            title={preset.title}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
                              ${Math.abs(scale - preset.value) < 0.01
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <input
                        type="range"
                        min="0.2"
                        max="1.5"
                        step="0.05"
                        value={scale}
                        onChange={(e) => setScale(parseFloat(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>

                    {/* Position Controls */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <Move className="w-4 h-4" />
                        Position
                      </label>

                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-slate-500">Horizontal (X)</span>
                            <span className="text-xs font-mono text-slate-500">{posX.toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.1"
                            max="0.9"
                            step="0.02"
                            value={posX}
                            onChange={(e) => setPosX(parseFloat(e.target.value))}
                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                            <span>Left</span>
                            <span>Right</span>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-xs text-slate-500">Vertical (Y)</span>
                            <span className="text-xs font-mono text-slate-500">{posY.toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="-0.1"
                            max="1.5"
                            step="0.02"
                            value={posY}
                            onChange={(e) => setPosY(parseFloat(e.target.value))}
                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                            <span>Top</span>
                            <span>Bottom</span>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => { setPosX(0.5); setPosY(0.5); }}
                        className="w-full py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        Center Design
                      </button>
                    </div>

                    {/* Rotation Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <RotateCw className="w-4 h-4" />
                          Rotation
                        </label>
                        <span className="text-sm font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                          {angle}°
                        </span>
                      </div>

                      <div className="flex gap-2">
                        {[0, 90, 180, 270].map(deg => (
                          <button
                            key={deg}
                            onClick={() => setAngle(deg)}
                            className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors
                              ${angle === deg
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                          >
                            {deg}°
                          </button>
                        ))}
                      </div>

                      <input
                        type="range"
                        min="0"
                        max="360"
                        step="5"
                        value={angle}
                        onChange={(e) => setAngle(parseInt(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>

                    {/* Info Box */}
                    {hasChanges() && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                        <strong>Note:</strong> The product mockup will update after saving.
                        It may take a few seconds for Printify to generate the new preview.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={loading || saving || removing || !hasDesign}
              className="flex items-center gap-2 px-3 py-2 text-sm sm:text-base sm:px-4 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>

            {/* Remove Design Button - only show when position has a design */}
            {hasDesign && (
              <button
                onClick={handleRemoveDesign}
                disabled={removing || saving || loading}
                className="flex items-center gap-2 px-3 py-2 text-sm sm:text-base sm:px-4 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {removing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </>
                )}
              </button>
            )}
          </div>

          <div className="flex gap-2 sm:gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={saving || removing}
              className="px-3 py-2 text-sm sm:text-base sm:px-4 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || saving || removing || !hasChanges() || !hasDesign}
              className="flex items-center gap-2 px-4 py-2 text-sm sm:text-base sm:px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlacementEditor;
